import { useState } from 'react';

export interface Tweaks {
  accent: 'orange' | 'magenta' | 'purple';
  density: 'compact' | 'regular' | 'spacious';
  theme: 'light' | 'dark';
}

const DEFAULTS: Tweaks = { accent: 'orange', density: 'regular', theme: 'light' };
const STORAGE_KEY = 'ts-tweaks';

function normaliseTweaks(value: unknown): Tweaks {
  const saved = value && typeof value === 'object' ? value as Partial<Tweaks> : {};
  return {
    accent: 'orange',
    density: 'regular',
    theme: saved.theme === 'dark' ? 'dark' : 'light',
  };
}

export function loadTweaks(): Tweaks {
  try {
    return normaliseTweaks(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}'));
  } catch {
    return DEFAULTS;
  }
}

/** Applies the supported appearance setting while keeping accent and density fixed. */
export function applyTweaks(t: Tweaks): void {
  const r = document.documentElement;
  r.setAttribute('data-accent', 'orange');
  r.setAttribute('data-density', 'regular');
  r.setAttribute('data-theme', t.theme);
  r.style.colorScheme = t.theme;
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    ?.setAttribute('content', t.theme === 'dark' ? '#0E1014' : '#FAFAF7');
}

export function useTweaks(): [Tweaks, <K extends keyof Tweaks>(k: K, v: Tweaks[K]) => void] {
  const [tweaks, setTweaks] = useState<Tweaks>(loadTweaks);
  const setTweak = <K extends keyof Tweaks>(k: K, v: Tweaks[K]) => {
    setTweaks((prev) => {
      const next = normaliseTweaks({ ...prev, [k]: v });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      applyTweaks(next);
      return next;
    });
  };
  return [tweaks, setTweak];
}
